import type { LocalizedDict } from '../index'

export type ReferralProgramAdminStrings = {
  fields: {
    commission: { label: string; suffix: string; hint: string }
    period: { label: string; suffix: string; hint: string }
    bonus: { label: string; suffix: string; hint: string }
  }
  loading: string
  saving: string
  copied: string
  copyLink: string
  defaultValue: string
  defaults: {
    title: string
    subtitle: string
    loadError: string
    invalidNumber: (label: string) => string
    notSet: (fallback: number) => string
    save: string
    saved: string
  }
  codes: {
    title: string
    cancel: string
    issueCodeToggle: string
    issueTo: string
    searchPlaceholder: string
    loadingDirectory: string
    noMatches: string
    issuing: string
    issueCode: string
    codeIssued: string
    shareThisLink: string
    dismiss: string
    empty: string
    columns: {
      code: string
      status: string
      commission: string
      period: string
      bonus: string
      issued: string
      actions: string
    }
    active: string
    deactivated: string
    daysShort: (days: number | string) => string
    override: string
    deactivate: string
    customTermsFor: string
    customTermsHint: string
    commissionPercent: string
    periodDays: string
    bonusUsd: string
    saveOverrides: string
  }
  activations: {
    title: string
    subtitle: string
    empty: string
    unknown: string
    activated: (date: string) => string
    approve: string
  }
  page: {
    title: string
    descBefore: string
    descAfter: string
  }
}

export const referralProgramAdminI18n: LocalizedDict<ReferralProgramAdminStrings> = {
  en: {
    fields: {
      commission: {
        label: 'Commission',
        suffix: '% of order revenue',
        hint: 'Applied to every settled order of a referred partner.',
      },
      period: {
        label: 'Earning period',
        suffix: 'days per referral',
        hint: 'Counted from the referral’s approved activation.',
      },
      bonus: {
        label: 'Welcome bonus',
        suffix: 'USD',
        hint: 'One-time bonus on activation (issuance flow V2).',
      },
    },
    loading: 'Loading…',
    saving: 'Saving…',
    copied: '✓ Copied',
    copyLink: 'Copy link',
    defaultValue: 'default',
    defaults: {
      title: 'Older codes',
      subtitle: 'For codes issued before the rewards program, unless a code has its own value',
      loadError: "Couldn't load config.",
      invalidNumber: (label) => `${label}: enter a valid number`,
      notSet: (fallback) => `Not set — engine falls back to ${fallback}.`,
      save: 'Save defaults',
      saved: '✓ Saved',
    },
    codes: {
      title: 'Referral codes',
      cancel: 'Cancel',
      issueCodeToggle: '+ Issue code',
      issueTo: 'Issue a code to',
      searchPlaceholder: 'Search by name, email, or organization…',
      loadingDirectory: 'Loading directory…',
      noMatches: 'No matches.',
      issuing: 'Issuing…',
      issueCode: 'Issue code',
      codeIssued: 'Code issued:',
      shareThisLink: '— share this link:',
      dismiss: 'Dismiss',
      empty: 'No referral codes issued yet.',
      columns: {
        code: 'Code',
        status: 'Status',
        commission: 'Commission',
        period: 'Period',
        bonus: 'Bonus',
        issued: 'Issued',
        actions: 'Actions',
      },
      active: 'Active',
      deactivated: 'Deactivated',
      daysShort: (days) => `${days}d`,
      override: 'Override',
      deactivate: 'Deactivate',
      customTermsFor: 'Custom terms for',
      customTermsHint: '— leave a field empty to keep its current value',
      commissionPercent: 'Commission %',
      periodDays: 'Period (days)',
      bonusUsd: 'Bonus (USD)',
      saveOverrides: 'Save overrides',
    },
    activations: {
      title: 'Pending activation approvals',
      subtitle: 'Commission starts only after approval (fraud gate)',
      empty: 'Nothing waiting for approval.',
      unknown: '(unknown)',
      activated: (date) => `· activated ${date}`,
      approve: 'Approve',
    },
    page: {
      title: 'Referral Program',
      descBefore:
        'Issue referral codes, set the commission numbers, and approve activations. Referrers share',
      descAfter: "; the nightly worker credits commission on their referrals' settled orders.",
    },
  },
  tr: {
    fields: {
      commission: {
        label: 'Komisyon',
        suffix: '% sipariş cirosundan',
        hint: 'Davet edilen iş ortağının kesinleşen her siparişine uygulanır.',
      },
      period: {
        label: 'Kazanç süresi',
        suffix: 'gün (davet başına)',
        hint: 'Davetin onaylanan aktivasyonundan itibaren sayılır.',
      },
      bonus: {
        label: 'Hoş geldin bonusu',
        suffix: 'USD',
        hint: 'Aktivasyonda tek seferlik bonus (verme akışı V2).',
      },
    },
    loading: 'Yükleniyor…',
    saving: 'Kaydediliyor…',
    copied: '✓ Kopyalandı',
    copyLink: 'Bağlantıyı kopyala',
    defaultValue: 'varsayılan',
    defaults: {
      title: 'Eski kodlar',
      subtitle: 'Ödül programından önce verilen kodlar için; kodun kendi değeri yoksa',
      loadError: 'Ayarlar yüklenemedi.',
      invalidNumber: (label) => `${label}: geçerli bir sayı gir`,
      notSet: (fallback) => `Ayarlanmadı — sistem ${fallback} değerini kullanır.`,
      save: 'Varsayılanları kaydet',
      saved: '✓ Kaydedildi',
    },
    codes: {
      title: 'Referans kodları',
      cancel: 'Vazgeç',
      issueCodeToggle: '+ Kod ver',
      issueTo: 'Kodu kime vereceksin',
      searchPlaceholder: 'Ad, e-posta veya şirkete göre ara…',
      loadingDirectory: 'Kullanıcılar yükleniyor…',
      noMatches: 'Eşleşme yok.',
      issuing: 'Veriliyor…',
      issueCode: 'Kod ver',
      codeIssued: 'Kod verildi:',
      shareThisLink: '— bu bağlantıyı paylaş:',
      dismiss: 'Kapat',
      empty: 'Henüz referans kodu verilmedi.',
      columns: {
        code: 'Kod',
        status: 'Durum',
        commission: 'Komisyon',
        period: 'Süre',
        bonus: 'Bonus',
        issued: 'Verildi',
        actions: 'İşlemler',
      },
      active: 'Aktif',
      deactivated: 'Devre dışı',
      daysShort: (days) => `${days} gün`,
      override: 'Özel ayar',
      deactivate: 'Devre dışı bırak',
      customTermsFor: 'Özel koşullar:',
      customTermsHint: '— mevcut değeri korumak için alanı boş bırak',
      commissionPercent: 'Komisyon %',
      periodDays: 'Süre (gün)',
      bonusUsd: 'Bonus (USD)',
      saveOverrides: 'Özel ayarları kaydet',
    },
    activations: {
      title: 'Onay bekleyen aktivasyonlar',
      subtitle: 'Komisyon yalnızca onaydan sonra başlar (dolandırıcılık kontrolü)',
      empty: 'Onay bekleyen bir şey yok.',
      unknown: '(bilinmiyor)',
      activated: (date) => `· ${date} tarihinde aktifleşti`,
      approve: 'Onayla',
    },
    page: {
      title: 'Referans Programı',
      descBefore:
        'Referans kodu ver, komisyon rakamlarını belirle ve aktivasyonları onayla. Davet edenler şunu paylaşır:',
      descAfter: '; gece çalışan işlem, davet ettiklerinin kesinleşen siparişleri için komisyonu yatırır.',
    },
  },
}
